// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as validate from '../validation';
import { FieldValidator, enumValidator } from '../validation';
import { ApiEnvironment } from '../vsCodeEduClientExt';
import * as assert from './assert';

export const VSCodeEduDirectoryName = '/VSCode Edu';
export const VSCodeEduDirectoryMatcherRegExp = new RegExp(`^[\\/]?${VSCodeEduDirectoryName}`);

/**
 * Note: these are also used as string values in vscode-dev.
 * No changes must be made to this enum without also updating vscode-dev.
 */
export enum KnownQueryParams {
    CourseId = 'courseId',
    ExtensionVersion = 'extension',
    Environment = 'env',
    Profile = 'profile',
    VSCodeCOI = 'vscode-coi',
    Workspaces = 'workspaces',
    DevWorkspace = 'workspace-scheme',
    DevWorkspacePath = 'workspace-path',
    ProjectId = 'projectId',
    NewAuth = 'newAuth',
    HomePageVersion = 'homePageVersion',
    SharedLinkId = 'sharedLinkId',
    CurriculumId = 'curriculumId',
    SignOut = 'sign-out',
    GithubRepo = 'githubRepo',
    VSCodeVersion = 'vscode-version',
}

export enum KnownHashParams {
    AccountId = 'account-id',
    SelectCourseNode = 'select-course-node',
}

/**
 * Note: these are also used as string values in vscode-dev.
 * No changes must be made to this enum without also updating vscode-dev.
 */
export enum KnownAuthQueryParams {
    Otac = 'otac',
    RequestId = 'requestId',
    CodeChallenge = 'codeChallenge',
    RedirectUri = 'redirectUri',
    Region = 'region',
}

// Possible values for the 'workspace-scheme' URL query string parameter.
export enum WorkspaceScheme {
    Workspace = 'vscode-edu-workspace',
    Internal = 'vscode-edu-internal',
}

// Represents the query string parameters of a vscode.dev/edu URL.
export type VSCodeUrlParams = Partial<{
    [key in keyof Omit<
        VSCodeUrl,
        'toString' | 'forCourse' | 'forProject' | 'forSharedProject' | 'forCurriculum'
    >]: VSCodeUrl[key];
}>;

// Represents a vscode.dev/edu URL with strongly-typed validated query string parameters.
export class VSCodeUrl {
    private static validateWorkspaceScheme = enumValidator(Object.values(WorkspaceScheme));
    private readonly url: URL;

    // Creates a new vscode.dev URL with the specified parameters.
    constructor(url: ConstructorParameters<typeof URL>[0] | ApiEnvironment, params?: VSCodeUrlParams) {
        switch (url) {
            case ApiEnvironment.Production:
                this.url = new URL('https://vscode.dev/edu');
                break;
            case ApiEnvironment.Beta:
                this.url = new URL('https://vscode.dev/edu?env=beta');
                break;
            case ApiEnvironment.PreProduction:
            case ApiEnvironment.LocalHost:
                this.url = new URL('https://insiders.vscode.dev/edu?env=ppe');
                break;
            default:
                this.url = new URL(url);
                break;
        }

        if (params !== undefined) {
            Object.assign(this, params);
        }
    }

    // Creates vscode.dev URL for the specified course.
    public static forCourse(environment: ApiEnvironment, courseId: string, params?: VSCodeUrlParams): VSCodeUrl {
        return new VSCodeUrl(environment, {
            courseId,
            workspaceScheme: WorkspaceScheme.Workspace,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified project.
    public static forProject(environment: ApiEnvironment, projectId: string, params?: VSCodeUrlParams): VSCodeUrl {
        return new VSCodeUrl(environment, {
            projectId,
            profile: 'project',
            workspaceScheme: WorkspaceScheme.Workspace,
            workspacePath: VSCodeEduDirectoryName,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified shared project link.
    public static forSharedProject(
        environment: ApiEnvironment,
        sharedLinkId: string,
        params?: VSCodeUrlParams
    ): VSCodeUrl {
        return new VSCodeUrl(environment, {
            sharedLinkId,
            profile: 'project',
            workspaceScheme: WorkspaceScheme.Workspace,
            workspacePath: VSCodeEduDirectoryName,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified curriculum.
    public static forCurriculum(
        environment: ApiEnvironment,
        curriculumId: string,
        githubRepo: string,
        params?: VSCodeUrlParams
    ): VSCodeUrl {
        return new VSCodeUrl(environment, {
            curriculumId,
            profile: 'curriculum',
            workspaceScheme: WorkspaceScheme.Workspace,
            githubRepo,
            ...params,
        });
    }

    // Returns string representation of the URL.
    public toString(): string {
        const value = this.url.toString();
        return value.endsWith('?') ? value.slice(0, -1) : value;
    }

    public get environment(): ApiEnvironment {
        switch (this.getParam(KnownQueryParams.Environment)?.toLowerCase()) {
            case 'ppe':
                return ApiEnvironment.PreProduction;
            case 'beta':
                return ApiEnvironment.Beta;
            default:
                return ApiEnvironment.Production;
        }
    }
    public set environment(value: ApiEnvironment) {
        switch (value) {
            case ApiEnvironment.PreProduction:
            case ApiEnvironment.LocalHost:
                this.url.hostname = 'insiders.vscode.dev';
                this.url.searchParams.set(KnownQueryParams.Environment, 'ppe');
                break;
            case ApiEnvironment.Beta:
                this.url.hostname = 'vscode.dev';
                this.url.searchParams.set(KnownQueryParams.Environment, 'beta');
                break;
            default:
                this.url.hostname = 'vscode.dev';
                this.url.searchParams.delete(KnownQueryParams.Environment);
                break;
        }
    }

    public get extensionVersion(): string | undefined {
        return this.getParam(KnownQueryParams.ExtensionVersion);
    }
    public set extensionVersion(value: string | undefined) {
        this.setParam(KnownQueryParams.ExtensionVersion, value);
    }

    public get homePageVersion(): string | undefined {
        return this.getParam(KnownQueryParams.HomePageVersion);
    }
    public set homePageVersion(value: string | undefined) {
        this.setParam(KnownQueryParams.HomePageVersion, value);
    }

    public get courseId(): string | undefined {
        return this.getParam(KnownQueryParams.CourseId, validate.uniqueIdPart);
    }
    public set courseId(value: string | undefined) {
        this.setParam(KnownQueryParams.CourseId, value, validate.uniqueIdPart);
    }

    public get projectId(): string | undefined {
        return this.getParam(KnownQueryParams.ProjectId, validate.uniqueIdPart);
    }
    public set projectId(value: string | undefined) {
        this.setParam(KnownQueryParams.ProjectId, value, validate.uniqueIdPart);
    }

    public get sharedLinkId(): string | undefined {
        return this.getParam(KnownQueryParams.SharedLinkId, validate.sharedLinkId);
    }
    public set sharedLinkId(value: string | undefined) {
        this.setParam(KnownQueryParams.SharedLinkId, value, validate.sharedLinkId);
    }

    public get profile(): string | undefined {
        return this.getParam(KnownQueryParams.Profile, validate.profileName);
    }
    public set profile(value: string | undefined) {
        this.setParam(KnownQueryParams.Profile, value, validate.profileName);
    }

    public get workspaceScheme(): WorkspaceScheme | undefined {
        const value = this.getParam(KnownQueryParams.DevWorkspace);
        return VSCodeUrl.validateWorkspaceScheme(value, 'value') === undefined ? (value as WorkspaceScheme) : undefined;
    }
    public set workspaceScheme(value: WorkspaceScheme | undefined) {
        this.setParam(KnownQueryParams.DevWorkspace, value, VSCodeUrl.validateWorkspaceScheme);
    }

    public get workspacePath(): string | undefined {
        return this.getParam(KnownQueryParams.DevWorkspacePath);
    }
    public set workspacePath(value: string | undefined) {
        this.setParam(KnownQueryParams.DevWorkspacePath, value);
    }

    public get accountId() {
        return this.getHashParam(KnownHashParams.AccountId, validate.uuid);
    }
    public set accountId(value: string | undefined) {
        this.setHashParam(KnownHashParams.AccountId, value, validate.uuid);
    }

    public get selectCourseNode() {
        return this.getHashParam(KnownHashParams.SelectCourseNode);
    }
    public set selectCourseNode(value: string | undefined) {
        this.setHashParam(KnownHashParams.SelectCourseNode, value);
    }

    public get codeChallenge() {
        return this.getParam(KnownAuthQueryParams.CodeChallenge, validate.codeVerifier);
    }
    public set codeChallenge(value: string | undefined) {
        this.setParam(KnownAuthQueryParams.CodeChallenge, value, validate.codeVerifier);
    }

    public get otac() {
        return this.getParam(KnownAuthQueryParams.Otac, validate.otac);
    }
    public set otac(value: string | undefined) {
        this.setParam(KnownAuthQueryParams.Otac, value, validate.otac);
    }

    public get redirectUri() {
        return this.getParam(KnownAuthQueryParams.RedirectUri, validate.absoluteUrl);
    }
    public set redirectUri(value: string | undefined) {
        this.setParam(KnownAuthQueryParams.RedirectUri, value, validate.absoluteUrl);
    }

    public get region() {
        return this.getParam(KnownAuthQueryParams.Region);
    }
    public set region(value: string | undefined) {
        this.setParam(KnownAuthQueryParams.Region, value);
    }

    public get requestId() {
        return this.getParam(KnownAuthQueryParams.RequestId, validate.uuid);
    }
    public set requestId(value: string | undefined) {
        this.setParam(KnownAuthQueryParams.RequestId, value, validate.uuid);
    }

    public get signOut() {
        return this.getParam(KnownQueryParams.SignOut);
    }
    public set signOut(value: string | undefined) {
        this.setParam(KnownQueryParams.SignOut, value);
    }

    public get curriculumId() {
        return this.getParam(KnownQueryParams.CurriculumId, validate.uniqueIdPart);
    }
    public set curriculumId(value: string | undefined) {
        this.setParam(KnownQueryParams.CurriculumId, value, validate.uniqueIdPart);
    }

    public get githubRepo() {
        return this.getParam(KnownQueryParams.GithubRepo, validate.githubRepo);
    }
    public set githubRepo(value: string | undefined) {
        this.setParam(KnownQueryParams.GithubRepo, value, validate.githubRepo);
    }

    public get vscodeVersion() {
        return this.getParam(KnownQueryParams.VSCodeVersion);
    }
    public set vscodeVersion(value: string | undefined) {
        this.setParam(KnownQueryParams.VSCodeVersion, value);
    }

    // Retrieves and validates a query string parameter.
    private getParam(param: string, validator?: FieldValidator): string | undefined {
        const value = this.url.searchParams.get(param);
        if (value === null || value.length === 0) {
            return undefined;
        } else if (validator?.(value, 'param') !== undefined) {
            return undefined;
        } else {
            return value;
        }
    }

    // Validates, then sets a query string parameter.
    private setParam(param: string, value?: string, validator?: FieldValidator): void {
        if (value === undefined || value.length === 0) {
            this.url.searchParams.delete(param);
        } else {
            assert.isValid(validator?.(value, param));
            this.url.searchParams.set(param, value);
        }
    }

    // Retrieves and validates a hash parameter.
    private getHashParam(param: string, validator?: FieldValidator): string | undefined {
        const hash = new URLSearchParams(this.url.hash.slice(1));
        const value = hash.get(param);
        if (value === null || value.length === 0) {
            return undefined;
        } else if (validator?.(value, 'param') !== undefined) {
            return undefined;
        } else {
            return value;
        }
    }

    // Validates, then sets a hash parameter.
    private setHashParam(param: string, value?: string, validator?: FieldValidator): void {
        const hash = new URLSearchParams(this.url.hash.slice(1));
        if (value === undefined || value.length === 0) {
            hash.delete(param);
        } else {
            assert.isValid(validator?.(value, param));
            hash.set(param, value);
        }
        this.url.hash = hash.toString();
    }
}
